import React, { useEffect, useState } from "react";
import { Tooltip } from "@mui/material";
import Divider from "@mui/material/Divider";
import { Lock, Unlock, Trash2 } from "lucide-react";
import removeCursor from "../../../../assets/icons/circle.svg";
import GenericDialog from "../../../Dialog/ConsentDialog";
import BackgroundColor from "../../BackgroundColor";
import IconLibrary from "../../IconLibrary/IconLibrary";
import { Image } from "../../../../Handlers/ToolsHandler";
import { useMenuContext, useCanvasContext } from "../../../../hooks";
import { iconToolsMaps, TOOL_CONSTANTS } from "../../../../constants";
import "./menu.scss";

const ICON_SIZE = 18;

const Menu = () => {
  const { activeTool, setActiveTool, lockStatus, setLockStatus } =
    useMenuContext();
  const { canvas } = useCanvasContext();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isOpenBackground, setIsOpenBackground] = useState(false);
  const [isOpenIcons, setIsOpenIcons] = useState(false);

  useEffect(() => {
    if (!canvas) return;
    if (activeTool === TOOL_CONSTANTS.MARKER) {
      canvas.isDrawingMode = true;
      canvas.freeDrawingBrush.width = 3;
    } else canvas.isDrawingMode = false;
    handleToolsSettings(canvas, activeTool, {
      openBg: () => setIsOpenBackground(true),
      openIcons: () => setIsOpenIcons(true),
    });
  }, [activeTool, lockStatus, canvas]);

  const onToolClick = (tool, event) => {
    // Picking another tool is a deliberate switch-away: if a text is still
    // being edited, exit editing so an empty IText gets cleaned up by the
    // text:editing:exited handler. Without this it lingers invisibly on the
    // canvas — most noticeable when the lock keeps the Text tool active.
    // (The automatic Cursor switch after creating a shape/text goes through
    // setActiveTool directly, not onToolClick, so it is unaffected.)
    const active = canvas?.getActiveObject();
    if (active && active.isEditing) {
      active.exitEditing();
    }
    setActiveTool(tool);
    setAnchorEl(event?.currentTarget);
  };

  const handleLock = () => {
    setLockStatus(!lockStatus);
  };

  const handleDelete = () => {
    canvas.clear();
    setIsDeleteDialogOpen(false);
  };

  const handleBackgroundClose = () => {
    setIsOpenBackground(false);
    setActiveTool(TOOL_CONSTANTS.CURSOR);
  };

  const handleIconsClose = () => {
    setIsOpenIcons(false);
    setActiveTool(TOOL_CONSTANTS.CURSOR);
  };

  const LockToggleIcon = lockStatus ? Lock : Unlock;

  return (
    <>
      <div className="menu-container upper">
        <div className="menu-container__inner-container ">
          <Tooltip title={"Keep selected tool active after drawing"}>
            <LockToggleIcon
              className="menu-container__button"
              size={ICON_SIZE}
              onClick={handleLock}
            />
          </Tooltip>
          <Divider orientation="vertical" flexItem />
          {iconToolsMaps.map((tool, index) => {
            const ToolIcon = tool.icon;
            return (
              <Tooltip title={tool.title} key={index}>
                <ToolIcon
                  className={
                    activeTool === `${tool.id}`
                      ? "menu-container__button active"
                      : "menu-container__button"
                  }
                  size={ICON_SIZE}
                  onClick={(event) => onToolClick(tool.id, event)}
                />
              </Tooltip>
            );
          })}
          <Divider orientation="vertical" flexItem />
          <Tooltip title={"Delete entire canvas"}>
            <Trash2
              className="menu-container__button"
              size={ICON_SIZE}
              onClick={() => setIsDeleteDialogOpen(true)}
            />
          </Tooltip>
          <GenericDialog
            open={isDeleteDialogOpen}
            handleClose={() => setIsDeleteDialogOpen(false)}
            handleDelete={handleDelete}
          />
          <BackgroundColor
            open={isOpenBackground}
            anchorEl={anchorEl}
            onClose={handleBackgroundClose}
          />
          <IconLibrary
            open={isOpenIcons}
            anchorEl={anchorEl}
            onClose={handleIconsClose}
          />
        </div>
      </div>
    </>
  );
};

const handleToolsSettings = (canvas, tool, openers) => {
  switch (tool) {
    case TOOL_CONSTANTS.CURSOR:
      canvas.getObjects().forEach((obj) => {
        obj.set({ selectable: true });
      });
      canvas.selection = true;
      canvas.hoverCursor = "move";
      canvas.defaultCursor = "default";
      canvas.renderAll();
      break;
    case TOOL_CONSTANTS.IMAGE:
      Image(canvas);
      break;
    case TOOL_CONSTANTS.BACKGROUND_COLOR:
      openers.openBg();
      break;
    case TOOL_CONSTANTS.ICONS:
      // Keep the board selectable while the picker is open; closing it returns
      // to the cursor tool.
      canvas.getObjects().forEach((obj) => obj.set({ selectable: true }));
      canvas.selection = true;
      openers.openIcons();
      break;
    case TOOL_CONSTANTS.ERASER:
      canvas.getObjects().forEach((obj) => {
        obj.selectable = false;
      });
      canvas.isDrawingMode = false;
      canvas.hoverCursor = `url(${removeCursor}), auto`;
      canvas.defaultCursor = `url(${removeCursor}), auto`;
      canvas.discardActiveObject();
      canvas.renderAll();
      break;
    default:
      canvas.discardActiveObject();
      canvas.getObjects().forEach((obj) => {
        obj.set({ selectable: false });
      });
      canvas.hoverCursor = "cursor";
      canvas.defaultCursor = "crosshair";
      canvas.renderAll();
      break;
  }
};

export default Menu;
